import {Injectable} from '@angular/core';
import {Utils} from "../../utils/utils";
import {UserService} from "../../../user/user.service";
import {first, switchMap} from "rxjs/operators";
import {PluginData} from "./plugin-data";
import {WebsiteConnectHelperService} from "../website-connect-helper/website-connect-helper.service";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../store';
import {clientRoles} from "../../../user/user-roles-actions";
import {WorkspaceService} from "../../../workspace/workspace.service";
import {GlobalEventDispatcherService} from "../global-event-dispatcher/global-event-dispatcher.service";
import {GlobalEvent} from "../global-event-dispatcher/global-event";
import {GoogleAnalyticsService} from "../google-analytics/google-analytics.service";
import {BoosterService} from "../../../websites/website/booster/services/booster.service";
import {Observable} from "rxjs";
import {Website} from "../../../websites/website/website";
import {Workspace} from "../../../user-managment/workspace";
import {NotificationModalComponent} from "../../components/dialogs/notification-modal/notification-modal.component";
import {DbModalService} from "../../../modules/modal/modal";

@Injectable({
  providedIn: 'root'
})
export class PluginConnectService {
  private ownerWorkspaceId: any;
  private currentWorkspaceId: any;
  private currentWorkspaceRoleId: any;
  private _pluginData: PluginData | null = null;

  get pluginData(): PluginData {
    return this._pluginData;
  }

  constructor(
    private readonly _store: Store<fromStore.DbStoreState>,
    private readonly _userService: UserService,
    private readonly _websiteConnectHelperService: WebsiteConnectHelperService,
    private readonly _workspaceService: WorkspaceService,
    private readonly _boosterService: BoosterService,
    private _globalEventDispatcher: GlobalEventDispatcherService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _modalService: DbModalService
  ) {
    this._store.select(fromStore.getWorkspaceIdEntities).subscribe(workspaceIdEntities => {
      this.currentWorkspaceRoleId = workspaceIdEntities.currentWorkspaceRoleId;
      this.ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
      this.currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this.ownerWorkspaceId;
    });
  }

  public setPluginData(queryParams): void {
    if (!!queryParams.return_url) {
      this._pluginData = new PluginData(queryParams);
      localStorage.setItem('pluginData', JSON.stringify(this._pluginData));
    } else if (localStorage.getItem('pluginData')) {
      this._pluginData = JSON.parse(localStorage.getItem('pluginData'));
    }
  }

  public resetPluginData(): void {
    this._pluginData = null;
    localStorage.removeItem('pluginData');
  }

  public connect(email: string, workspaces?: Workspace[]): void {
    if (!email) return;

    if (this._pluginData.pluginName === '10webspeedoptimizer' && Utils.isUpdateAvailable(this._pluginData.version, '2.6.36')) {
      this._userService.showUpdateBoosterDialog(this._pluginData.returnUrl);
      this.resetPluginData();
      return;
    }
    if (this.currentWorkspaceRoleId !== clientRoles.OWNER && this.ownerWorkspaceId) {
      if (workspaces && workspaces.find(space => space.workspaceId === this.ownerWorkspaceId && space.status !== 'active')) {
        this.showConnectionFailModal();
        this.resetPluginData();
        return;
      }
      this._workspaceService.silentSwitchToOwnWorkspace()
        .then(() => this.handler(email));
    } else {
      this.handler(email);
    }
  }

  private handler(email): void {
    this._globalEventDispatcher.dispatchEvent(GlobalEvent.connectWebsite, true);
    if (this._pluginData.onlyOptimize) {
      this.getWebsites().subscribe(websites => {
        const IOConnectedWebsite = websites.find(({site_url}) => site_url === this._pluginData.siteUrl);
        if (IOConnectedWebsite) {
          this.runBoosterOptimization(IOConnectedWebsite.id);
        } else {
          this._globalEventDispatcher.dispatchEvent(GlobalEvent.connectWebsite, false);
        }
      });
    } else {
      this.getManagerToken(email);
    }
  }

  private runBoosterOptimization(websiteId: number): void {
    this._boosterService.runBoosterFlow(websiteId)
      .pipe(
        first(response => !!response)
      )
      .subscribe(response => {
        if (response.success) {
          this._websiteConnectHelperService.handleConnection(websiteId, this.pluginData.pluginName);
        }
        this.resetPluginData();
      });
  }

  private getManagerToken(email: string): void {
    this._userService.getManagerLoginToken(this._pluginData.siteUrl, this._pluginData.nonce, this._pluginData && this._pluginData.isNewConnectionFlow, this._pluginData.fromPlugin)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (!response.success) {
          return this.pluginConnectionFailed();
        }
        this._pluginData && this._pluginData.isNewConnectionFlow && this._store.dispatch(new fromStore.LoadWebsites());
        const connectedWebsiteId = response.data.connected_domain_id;
        if (this._pluginData && !this.pluginData.isNewConnectionFlow) {
          this.redirectToWP(response.data.token, email);
        } else if (!connectedWebsiteId) {
          this.handleEmptyWebsiteIdResponse();
        } else {
          this._pluginData && this._websiteConnectHelperService.handleConnection(connectedWebsiteId, this.pluginData.pluginName);
        }
        this.resetPluginData();
      });
  }

  private pluginConnectionFailed(): void {
    let returnUrl = this._pluginData && this._pluginData.returnUrl + '&connection_error=1';
    if (this._pluginData && this._pluginData.isNewConnectionFlow) {
      returnUrl += '&new_connection_flow=1';
    }
    this.resetPluginData();
    window.location.href = returnUrl;
  }

  private redirectToWP(token: string, email: string): void {

    let returnUrl = this._pluginData.returnUrl;
    returnUrl = Utils.addQueryString(returnUrl, 'nonce=' + this._pluginData.nonce);
    returnUrl = Utils.addQueryString(returnUrl, 'token=' + token);
    returnUrl = Utils.addQueryString(returnUrl, 'email=' + encodeURIComponent(email));
    returnUrl = Utils.addQueryString(returnUrl, 'logged_in=1');
    this.resetPluginData();

    window.location.href = returnUrl;
  }

  private handleEmptyWebsiteIdResponse(): void {
    const returnUrl = this._pluginData && this._pluginData.returnUrl + '&connection_error=1';
    this.resetPluginData();
    window.location.href = returnUrl;
  }

  private getWebsites(): Observable<Website[]> {
    return this._store.select(fromStore.getWebsitesLoaded)
      .pipe(
        first(loaded => loaded),
        switchMap(() => this._store.select(fromStore.getWebsites)),
        first()
      );
  }

  private showConnectionFailModal() {
    if (!this._modalService.hasOpenModals()) {
      const notifModal = this._modalService.open(NotificationModalComponent, {width: 500});
      notifModal.componentInstance.header = 'Unable to connect the website';
      notifModal.componentInstance.message = 'Connecting or migrating websites to 10Web dashboard is limited only to workspace owners.';
      notifModal.componentInstance.buttonTxt = 'OK';
    }
  }
}
